import { FieldConfig } from "./field-config-model";
import { FIELD_LABEL_DEFAULTS } from "./field-label-defaults";
import { StepType } from "./step-type.enum";

export class FieldConfigUtils {

  /**
   * Create a FieldConfig with automatic _i18n defaults from FIELD_LABEL_DEFAULTS.
   * Admins can still override per-language via the flow editor.
   */
  static create(name: string, displayName: string): FieldConfig {
    const fc = new FieldConfig(name, displayName);
    const translations = FIELD_LABEL_DEFAULTS[name];
    if (translations) {
      fc._i18n = {};
      for (const [lang, label] of Object.entries(translations)) {
        fc._i18n[lang] = { displayName: label };
      }
    }
    return fc;
  }

  public static generateAddressHistoryStepFieldConfig = (): any => {
    let addressHistoryStepFieldConfig: any = {};
    addressHistoryStepFieldConfig["residencyAddress.durationYears"] = FieldConfigUtils.create("residencyAddress.durationYears", "Year(s)");
    addressHistoryStepFieldConfig["residencyAddress.durationMonths"] = FieldConfigUtils.create("residencyAddress.durationMonths", "Month(s)");
    addressHistoryStepFieldConfig["residencyAddress.residencyBasis"] = FieldConfigUtils.create("residencyAddress.residencyBasis", "Ownership Status");
    addressHistoryStepFieldConfig["residencyAddress.rent"] = FieldConfigUtils.create("residencyAddress.rent", "Monthly Rent");
    addressHistoryStepFieldConfig["residencyAddress.moveInDate"] = FieldConfigUtils.create("residencyAddress.moveInDate", "Move-In Date");
    addressHistoryStepFieldConfig["residencyAddress.moveInDate"].isRequired = false;

    const reoFieldsConfig = this.generateReoStepFieldConfig();
    addressHistoryStepFieldConfig = { ...addressHistoryStepFieldConfig, ...reoFieldsConfig };
    return addressHistoryStepFieldConfig;
  }

  public static generateAssetsStepFieldConfig = (): any => {
    const assetsStepFieldConfig: any = {};
    assetsStepFieldConfig["asset.assetType"] = FieldConfigUtils.create("asset.assetType", "Asset Type");
    assetsStepFieldConfig["asset.depository"] = FieldConfigUtils.create("asset.depository", "Name of Bank/Credit Union");
    assetsStepFieldConfig["asset.cashMarketValue"] = FieldConfigUtils.create("asset.cashMarketValue", "Amount");
    assetsStepFieldConfig["asset.accountNumber"] = FieldConfigUtils.create("asset.accountNumber", "Account Number");
    assetsStepFieldConfig["asset.makeModel"] = FieldConfigUtils.create("asset.makeModel", "Make/Model");
    assetsStepFieldConfig["asset.year"] = FieldConfigUtils.create("asset.year", "Year");
    assetsStepFieldConfig["asset.disposition"] = FieldConfigUtils.create("asset.disposition", "Disposition");
    assetsStepFieldConfig["asset.amountOfMortgageLien"] = FieldConfigUtils.create("asset.amountOfMortgageLien", "Amount Of Mortgage Lien");
    assetsStepFieldConfig["asset.mortgagePayment"] = FieldConfigUtils.create("asset.mortgagePayment", "Mortgage Payment");
    assetsStepFieldConfig["asset.gross"] = FieldConfigUtils.create("asset.gross", "Gross");
    assetsStepFieldConfig["asset.netRentalIncome"] = FieldConfigUtils.create("asset.netRentalIncome", "Net Rental Income");
    assetsStepFieldConfig["asset.insMainTaxMisc"] = FieldConfigUtils.create("asset.insMainTaxMisc", "Insurance Maintainance Taxes Misc");
    assetsStepFieldConfig["asset.giftOrGrantSource"] = FieldConfigUtils.create("asset.giftOrGrantSource", "Source");
    assetsStepFieldConfig["asset.isDownPaymentSource"] = FieldConfigUtils.create("asset.isDownPaymentSource", "Is this cash gift being used for a down payment?");
    return assetsStepFieldConfig;
  }

  public static generatePurchaseCreditsStepFieldConfig = (): any => {
    const purchaseCreditsStepFieldConfig: any = {};
    purchaseCreditsStepFieldConfig["subjectProperty.presentValue"] = FieldConfigUtils.create("subjectProperty.presentValue", "Estimated Value");
    purchaseCreditsStepFieldConfig["subjectProperty.propertyWillBe"] = FieldConfigUtils.create("subjectProperty.propertyWillBe", "Property Will Be");
    purchaseCreditsStepFieldConfig["subjectProperty.state"] = FieldConfigUtils.create("subjectProperty.state", "State");
    purchaseCreditsStepFieldConfig["subjectProperty.yearBuilt"] = FieldConfigUtils.create("subjectProperty.yearBuilt", "Year Built");
    purchaseCreditsStepFieldConfig["subjectProperty.yearBuilt"].isRequired = false;
    purchaseCreditsStepFieldConfig["subjectProperty.noOfUnits"] = FieldConfigUtils.create("subjectProperty.noOfUnits", "Number of Units");
    purchaseCreditsStepFieldConfig["subjectProperty.noOfUnits"].isRequired = false;
    purchaseCreditsStepFieldConfig["mortgageApplication.extension.purchaseAgreementContractSignedDate"] =
      FieldConfigUtils.create("mortgageApplication.extension.purchaseAgreementContractSignedDate", "When was the contract signed?");
    purchaseCreditsStepFieldConfig["mortgageApplication.extension.purchaseAgreementExpectedCOEDate"] =
      FieldConfigUtils.create("mortgageApplication.extension.purchaseAgreementExpectedCOEDate", "When is the expected close of escrow?");

    purchaseCreditsStepFieldConfig["mortgageApplication.transactionDetail.purchaseCredits.earnestMoneyDeposit"] =
      FieldConfigUtils.create("mortgageApplication.transactionDetail.purchaseCredits.earnestMoneyDeposit", "How much is the earnest money deposit?");

    purchaseCreditsStepFieldConfig["mortgageApplication.transactionDetail.purchaseCredits.sellerCredits"] =
      FieldConfigUtils.create("mortgageApplication.transactionDetail.purchaseCredits.sellerCredits", "What is the amount?");

    return purchaseCreditsStepFieldConfig;
  }

  public static generateManufacturedHomeDetailsStepFieldConfig = (): any => {
    const manufacturedHomeDetailsStepFieldConfig: any = {};
    manufacturedHomeDetailsStepFieldConfig["subjectProperty.manufacturedHomeMake"] = FieldConfigUtils.create("subjectProperty.manufacturedHomeMake", "Make");
    manufacturedHomeDetailsStepFieldConfig["subjectProperty.manufacturedHomeModel"] = FieldConfigUtils.create("subjectProperty.manufacturedHomeModel", "Model");
    manufacturedHomeDetailsStepFieldConfig["subjectProperty.manufacturedHomePriceOfHome"] = FieldConfigUtils.create("subjectProperty.manufacturedHomePriceOfHome", "Price of Home");
    manufacturedHomeDetailsStepFieldConfig["subjectProperty.manufacturedHomeSalesPrice"] = FieldConfigUtils.create("subjectProperty.manufacturedHomeSalesPrice", "Sales Price");
    manufacturedHomeDetailsStepFieldConfig["subjectProperty.manufacturedHomeLotOwnershipType"] = FieldConfigUtils.create("subjectProperty.manufacturedHomeLotOwnershipType", "Lot Ownership Type");
    manufacturedHomeDetailsStepFieldConfig["subjectProperty.manufacturedHomeLotRentalAmount"] = FieldConfigUtils.create("subjectProperty.manufacturedHomeLotRentalAmount", "Lot Rental Amount");
    manufacturedHomeDetailsStepFieldConfig["subjectProperty.landType"] = FieldConfigUtils.create("subjectProperty.landType", "Land Type");
    manufacturedHomeDetailsStepFieldConfig["subjectProperty.sizeOfLand"] = FieldConfigUtils.create("subjectProperty.sizeOfLand", "Size of Land");
    manufacturedHomeDetailsStepFieldConfig["subjectProperty.estimatedLandValue"] = FieldConfigUtils.create("subjectProperty.estimatedLandValue", "Estimated Land Value");
    manufacturedHomeDetailsStepFieldConfig["subjectProperty.amountOwned"] = FieldConfigUtils.create("subjectProperty.amountOwned", "Amount Owed");
    return manufacturedHomeDetailsStepFieldConfig;
  }

  public static generateReviewApplicationStepFieldConfig = (): any => {
    const reviewApplicationStepFieldConfig: any = {};
    reviewApplicationStepFieldConfig["mortgage.transactionDetail.purchasePriceAmount"] = FieldConfigUtils.create("mortgage.transactionDetail.purchasePriceAmount", "Purchase Price");
    return reviewApplicationStepFieldConfig;
  }

  public static generateIncomeStepFieldConfig = (): any => {
    const incomeStepFieldConfig: any = {};
    incomeStepFieldConfig["employment.businessType"] = FieldConfigUtils.create("employment.businessType", "Business Type");
    incomeStepFieldConfig["employment.employer"] = FieldConfigUtils.create("employment.employer", "Employer Name");
    incomeStepFieldConfig["employment.employer.address.address1"] = FieldConfigUtils.create("employment.employer.address.address1", "Address");
    incomeStepFieldConfig["employment.employer.address.city"] = FieldConfigUtils.create("employment.employer.address.city", "City");
    incomeStepFieldConfig["employment.employer.address.state"] = FieldConfigUtils.create("employment.employer.address.state", "State");
    incomeStepFieldConfig["employment.employer.address.zipCode"] = FieldConfigUtils.create("employment.employer.address.zipCode", "Zip Code");
    incomeStepFieldConfig["employment.employer.address.country"] = FieldConfigUtils.create("employment.employer.address.country", "Country");
    incomeStepFieldConfig["employment.employer.address.county"] = FieldConfigUtils.create("employment.employer.address.county", "County");

    incomeStepFieldConfig["employment.isPrimary"] = FieldConfigUtils.create("employment.isPrimary", "Job Type");
    incomeStepFieldConfig["employment.position"] = FieldConfigUtils.create("employment.position", "Position");
    incomeStepFieldConfig["employment.employmentType"] = FieldConfigUtils.create("employment.employmentType", "Current or Former");
    incomeStepFieldConfig["employment.employerPhone"] = FieldConfigUtils.create("employment.employerPhone", "Corporate Phone");
    incomeStepFieldConfig["employment.yearsInLineOfWork"] = FieldConfigUtils.create("employment.yearsInLineOfWork", "Year(s) in Profession");
    incomeStepFieldConfig["employment.startDate"] = FieldConfigUtils.create("employment.startDate", "Start Date");
    incomeStepFieldConfig["employment.endDate"] = FieldConfigUtils.create("employment.endDate", "End Date");
    incomeStepFieldConfig["employment.selfEmploymentMonthlyIncomeOrLoss"] = FieldConfigUtils.create("employment.selfEmploymentMonthlyIncomeOrLoss", "Monthly Income/Loss");
    incomeStepFieldConfig["employment.borrowerOwnershipShare"] = FieldConfigUtils.create("employment.borrowerOwnershipShare", "Ownership Share");
    // LLS-369 — exact ownership percentage (maps to Encompass BusinessOwnedPercent, which survives
    // LOS sync) and derives the categorical Ownership Share bucket above. Visible + required by
    // default, matching the sibling field. Registering it is what makes it configurable at all:
    // GenericStepFactory prunes any saved config key missing from the generated default config, so
    // an unregistered field never reaches the flow editor and is stuck on the ConfigurableFieldHost
    // fallbacks (isHidden false / isRequired true) with no way for an admin to switch it off.
    incomeStepFieldConfig["employment.borrowerOwnershipSharePercent"] =
      FieldConfigUtils.create("employment.borrowerOwnershipSharePercent", "Ownership Percentage");
    incomeStepFieldConfig["employment.selfEmploymentForm"] = FieldConfigUtils.create("employment.selfEmploymentForm", "Self Employment Form");
    incomeStepFieldConfig["monthlyPay"] = FieldConfigUtils.create("monthlyPay", "Monthly Pay");
    incomeStepFieldConfig["employment.selfEmployed"] = FieldConfigUtils.create("employment.selfEmployed", "Self Employed");
    incomeStepFieldConfig["income.typeOfIncomeOtherDescription"] = FieldConfigUtils.create("income.typeOfIncomeOtherDescription", "Description");
    incomeStepFieldConfig["income.monthlyIncome"] = FieldConfigUtils.create("income.monthlyIncome", "Monthly Income");
    incomeStepFieldConfig["income.monthlyRent"] = FieldConfigUtils.create("income.monthlyRent", "Monthly Rent");
    return incomeStepFieldConfig;
  }

  public static generateReoStepFieldConfig = (): any => {
    const reoStepFieldConfig: any = {};
    reoStepFieldConfig["reo.typeOfProperty"] = FieldConfigUtils.create("reo.typeOfProperty", "Property Type");
    reoStepFieldConfig["reo.marketValue"] = FieldConfigUtils.create("reo.marketValue", "Property Value");
    reoStepFieldConfig["reo.currentUsageType"] = FieldConfigUtils.create("reo.currentUsageType", "How do you currently use this property?");
    reoStepFieldConfig["reo.intendedUsageType"] = FieldConfigUtils.create("reo.intendedUsageType", "How do you plan on using this property?");
    reoStepFieldConfig["reo.dispositionStatus"] = FieldConfigUtils.create("reo.dispositionStatus", "Do you plan on selling this property?");
    reoStepFieldConfig["reo.amountOfMortgage"] = FieldConfigUtils.create("reo.amountOfMortgage", "Current balance of existing liens on this property?");
    reoStepFieldConfig["reo.mortgagePayment"] = FieldConfigUtils.create("reo.mortgagePayment", "Monthly Payment");
    reoStepFieldConfig["reo.grossRentalIncome"] = FieldConfigUtils.create("reo.grossRentalIncome", "Gross Rental Income");
    reoStepFieldConfig["reo.purchasePrice"] = FieldConfigUtils.create("reo.purchasePrice", "Original Purchase Price");
    reoStepFieldConfig["reo.dateAcquired"] = FieldConfigUtils.create("reo.dateAcquired", "Date Acquired");
    reoStepFieldConfig["reo.hazardInsurance"] = FieldConfigUtils.create("reo.hazardInsurance", "Home Insurance");
    reoStepFieldConfig["reo.propertyTax"] = FieldConfigUtils.create("reo.propertyTax", "Property Tax");
    reoStepFieldConfig["reo.hoa"] = FieldConfigUtils.create("reo.hoa", "HOA");
    reoStepFieldConfig["reo.monthlyMiscExpenses"] = FieldConfigUtils.create("reo.monthlyMiscExpenses", "Monthly Misc. Expenses");
    return reoStepFieldConfig;
  }

  public static generateAddressStepFieldConfig = (): any => {
    // URLA 2020 — Loan & Property Info → Type of Refinance, Purpose of Refinance.
    // Surfaced for admin configuration on AddressStep instances but only renders at
    // runtime when the loan purpose is Refinance (gated in the component template).
    // Defaults to off (isHidden + not required) so existing flows are unaffected.
    const addressStepFieldConfig: any = {};
    addressStepFieldConfig["subjectProperty.refiPurpose"] =
      FieldConfigUtils.create("subjectProperty.refiPurpose", "Type of Refinance");
    addressStepFieldConfig["subjectProperty.refiPurpose"].isHidden = true;
    addressStepFieldConfig["subjectProperty.refiPurpose"].isRequired = false;
    addressStepFieldConfig["subjectProperty.refinancePrimaryPurpose"] =
      FieldConfigUtils.create("subjectProperty.refinancePrimaryPurpose", "Purpose of Refinance");
    addressStepFieldConfig["subjectProperty.refinancePrimaryPurpose"].isHidden = true;
    addressStepFieldConfig["subjectProperty.refinancePrimaryPurpose"].isRequired = false;
    return addressStepFieldConfig;
  }

  public static generateDeclarationsStepFieldConfig = (): any => {
    // One entry per declaration that carries an explanation. Default isHidden=true so existing
    // flows behave exactly like before (no new visible fields, no new validation). An admin
    // flipping Hidden→off enables the explanation textarea with isRequired=true by default.
    const declarationsFieldConfig: any = {};
    const entries: Array<[string, string]> = [
      ["borrower.declarations.borrowedDownPayment.explanation",                  "Borrowed Down Payment Explanation"],
      ["borrower.declarations.coSignedLoan.explanation",                         "Co-Signed Loan Explanation"],
      ["borrower.declarations.haveJudgement.explanation",                        "Outstanding Judgments Explanation"],
      ["borrower.declarations.haveTaxDebt.explanation",                          "Federal Debt Default Explanation"],
      ["borrower.declarations.haveLawsuit.explanation",                          "Lawsuit Explanation"],
      ["borrower.declarations.haveConveyedTitleInLieuOfForeclosure.explanation", "Conveyed Title in Lieu of Foreclosure Explanation"],
      ["borrower.declarations.haveCompletedShortSale.explanation",               "Pre-Foreclosure / Short Sale Explanation"],
      ["borrower.declarations.beenForclosed.explanation",                        "Foreclosure Explanation"],
      ["borrower.declarations.declaredBankruptcy.explanation",                   "Bankruptcy Explanation"],
    ];
    for (const [name, displayName] of entries) {
      const fc = FieldConfigUtils.create(name, displayName);
      fc.isHidden = true;
      declarationsFieldConfig[name] = fc;
    }
    return declarationsFieldConfig;
  }

  public static generateLoanInfoStepFieldConfig = (): any => {
    const loanInfoStepFieldConfig: any = {};
    loanInfoStepFieldConfig["mortgageApplication.mortgageTerm.mortgageAppliedFor"] =
      FieldConfigUtils.create("mortgageApplication.mortgageTerm.mortgageAppliedFor", "If you do, please choose it from the list below. If not, please click continue.");
    loanInfoStepFieldConfig["mortgage.transactionDetail.purchasePriceAmount"] = FieldConfigUtils.create("mortgage.transactionDetail.purchasePriceAmount", "Purchase Price");
    loanInfoStepFieldConfig["mortgage.transactionDetail.downpayment"] =
      FieldConfigUtils.create("mortgage.transactionDetail.downpayment", "Down Payment"); // mortgage.transactionDetail.downpayment does not really exist on mortgage model
    loanInfoStepFieldConfig['mortgage.subjectProperty.downPaymentSource'] = FieldConfigUtils.create('mortgage.subjectProperty.downPaymentSource', 'Down Payment Source');
    // LLS-218 — Estimated Value keeps today's behavior (shown + required where it renders today).
    loanInfoStepFieldConfig['mortgage.subjectProperty.presentValue'] = FieldConfigUtils.create('mortgage.subjectProperty.presentValue', 'Estimated Value');
    // LLS-218 — Appraised Value is new; default off (hidden + not required) so existing flows are unaffected. Admins opt in.
    loanInfoStepFieldConfig['mortgage.mortgageTerm.appraisedValue'] = FieldConfigUtils.create('mortgage.mortgageTerm.appraisedValue', 'Appraised Value');
    loanInfoStepFieldConfig['mortgage.mortgageTerm.appraisedValue'].isHidden = true;
    loanInfoStepFieldConfig['mortgage.mortgageTerm.appraisedValue'].isRequired = false;
    return loanInfoStepFieldConfig;
  }

  // WI 24451 — unmarried-partner conditional group (My Info + co-borrower; rendered only when
  // maritalStatus is Single or Other — see allowsUnmarriedPartner). Default OFF (hidden + not required) for backward compat — admins opt in
  // per flow. The gate ("unmarriedAddendum") is a checkbox, so its Required toggle is a no-op; Show/Required
  // apply to Type/State/Specify once enabled.
  private static generateUnmarriedPartnerFieldsConfig = (): any => {
    const cfg: any = {};
    cfg["borrower.unmarriedAddendum"] = FieldConfigUtils.create("borrower.unmarriedAddendum", "Has unmarried partner with real property rights");
    cfg["borrower.domesticRelationshipType"] = FieldConfigUtils.create("borrower.domesticRelationshipType", "Relationship Type");
    cfg["borrower.unmarriedRelationshipState"] = FieldConfigUtils.create("borrower.unmarriedRelationshipState", "Relationship State");
    cfg["borrower.unmarriedRelationshipTypeOther"] = FieldConfigUtils.create("borrower.unmarriedRelationshipTypeOther", "Specify Relationship Type");
    for (const key of Object.keys(cfg)) {
      cfg[key].isHidden = true;
      cfg[key].isRequired = false;
    }
    return cfg;
  }

  public static generateBorrowerFieldsConfig = (): any => {
    let borrowerFieldsConfig: any = {};
    borrowerFieldsConfig["borrower.firstName"] = FieldConfigUtils.create("borrower.firstName", "First Name");
    borrowerFieldsConfig["borrower.lastName"] = FieldConfigUtils.create("borrower.lastName", "Last Name");
    borrowerFieldsConfig["borrower.middleName"] = FieldConfigUtils.create("borrower.middleName", "Middle Name");
    borrowerFieldsConfig["borrower.middleName"].isRequired = false;
    borrowerFieldsConfig["borrower.nameSuffix"] = FieldConfigUtils.create("borrower.nameSuffix", "Suffix");
    borrowerFieldsConfig["borrower.nameSuffix"].isRequired = false;
    borrowerFieldsConfig["borrower.mobilePhone"] = FieldConfigUtils.create("borrower.mobilePhone", "Phone Number");
    // Default off (hidden + not required) for backward compat — admins opt in per flow.
    borrowerFieldsConfig["borrower.homePhone"] = FieldConfigUtils.create("borrower.homePhone", "Home Phone");
    borrowerFieldsConfig["borrower.homePhone"].isHidden = true;
    borrowerFieldsConfig["borrower.homePhone"].isRequired = false;
    borrowerFieldsConfig["borrower.dateOfBirth"] = FieldConfigUtils.create("borrower.dateOfBirth", "Birth Date");
    borrowerFieldsConfig["borrower.socialSecNum"] = FieldConfigUtils.create("borrower.socialSecNum", "Social Security Number");
    borrowerFieldsConfig["borrower.declarations.residenceStatus"] = FieldConfigUtils.create("borrower.declarations.residenceStatus", "Citizenship");
    borrowerFieldsConfig["borrower.maritalStatus"] = FieldConfigUtils.create("borrower.maritalStatus", "Marital Status");
    borrowerFieldsConfig["borrower.numberOfDependents"] = FieldConfigUtils.create("borrower.numberOfDependents", "Number of Dependents");
    borrowerFieldsConfig["borrower.languagePreference"] = FieldConfigUtils.create("borrower.languagePreference", "Language Preference");
    borrowerFieldsConfig["borrower.languagePreferenceOtherDescription"] =
      FieldConfigUtils.create("borrower.languagePreferenceOtherDescription", "Specify Language");
    borrowerFieldsConfig["borrower.dependentAges"] = FieldConfigUtils.create("borrower.dependentAges", "Dependent Ages");
    borrowerFieldsConfig = { ...borrowerFieldsConfig, ...FieldConfigUtils.generateUnmarriedPartnerFieldsConfig() };
    return borrowerFieldsConfig;
  }

  public static generateCoBorrowerFieldsConfig = (): any => {
    let borrowerFieldsConfig: any = {};
    borrowerFieldsConfig["borrower.firstName"] = FieldConfigUtils.create("borrower.firstName", "First Name");
    borrowerFieldsConfig["borrower.lastName"] = FieldConfigUtils.create("borrower.lastName", "Last Name");
    borrowerFieldsConfig["borrower.middleName"] = FieldConfigUtils.create("borrower.middleName", "Middle Name");
    borrowerFieldsConfig["borrower.middleName"].isRequired = false;
    borrowerFieldsConfig["borrower.nameSuffix"] = FieldConfigUtils.create("borrower.nameSuffix", "Suffix");
    borrowerFieldsConfig["borrower.nameSuffix"].isRequired = false;
    borrowerFieldsConfig["borrower.primaryEmail"] = FieldConfigUtils.create("borrower.primaryEmail", "E-mail");
    borrowerFieldsConfig["borrower.mobilePhone"] = FieldConfigUtils.create("borrower.mobilePhone", "Phone Number");
    borrowerFieldsConfig["borrower.dateOfBirth"] = FieldConfigUtils.create("borrower.dateOfBirth", "Birth Date");
    borrowerFieldsConfig["borrower.socialSecNum"] = FieldConfigUtils.create("borrower.socialSecNum", "Social Security Number");
    borrowerFieldsConfig["borrower.declarations.residenceStatus"] = FieldConfigUtils.create("borrower.declarations.residenceStatus", "Citizenship");
    borrowerFieldsConfig["borrower.maritalStatus"] = FieldConfigUtils.create("borrower.maritalStatus", "Marital Status");
    borrowerFieldsConfig["borrower.languagePreference"] = FieldConfigUtils.create("borrower.languagePreference", "Language Preference");
    borrowerFieldsConfig = { ...borrowerFieldsConfig, ...FieldConfigUtils.generateUnmarriedPartnerFieldsConfig() };
    return borrowerFieldsConfig;
  }

  public static generateAddressFieldsConfig = (): any => {
    const addressFieldsConfig: any = {};
    addressFieldsConfig["residencyAddress.durationYears"] = FieldConfigUtils.create("residencyAddress.durationYears", "Year(s)");
    addressFieldsConfig["residencyAddress.durationMonths"] = FieldConfigUtils.create("residencyAddress.durationMonths", "Month(s)");
    addressFieldsConfig["residencyAddress.residencyBasis"] = FieldConfigUtils.create("residencyAddress.residencyBasis", "Ownership Status");
    addressFieldsConfig["residencyAddress.rent"] = FieldConfigUtils.create("residencyAddress.rent", "Monthly Payment");
    addressFieldsConfig["residencyAddress.moveInDate"] = FieldConfigUtils.create("residencyAddress.moveInDate", "Move-In Date");
    addressFieldsConfig["residencyAddress.moveInDate"].isRequired = false;
    return addressFieldsConfig;
  }

  /**
   * LLS-569 item 2: Create Account borrower fields. E-mail (the user id) and the password fields
   * are intentionally NOT here. Phone Number was never required on this step, so it ships
   * not-required to keep existing flows from starting to block.
   */
  public static generateCreateAccountStepFieldConfig = (): any => {
    const createAccountStepFieldConfig: any = {};
    createAccountStepFieldConfig["borrower.firstName"] = FieldConfigUtils.create("borrower.firstName", "First Name");
    createAccountStepFieldConfig["borrower.lastName"] = FieldConfigUtils.create("borrower.lastName", "Last Name");
    createAccountStepFieldConfig["borrower.mobilePhone"] = FieldConfigUtils.create("borrower.mobilePhone", "Phone Number");
    createAccountStepFieldConfig["borrower.mobilePhone"].isRequired = false;
    createAccountStepFieldConfig["borrower.phoneType"] = FieldConfigUtils.create("borrower.phoneType", "Phone Type");
    createAccountStepFieldConfig["borrower.phoneType"].isRequired = false;
    return createAccountStepFieldConfig;
  }

  public static generateDefaultFieldConfigForStep = (stepType: StepType): any => {
    let fieldConfig: any = {}
    if (stepType === StepType.MyInfoStep) {
      // My info has no generated field config; the empty default above is intentional.
    } else if (stepType === StepType.ManualIncomeAndEmploymentStep) {
      fieldConfig = FieldConfigUtils.generateIncomeStepFieldConfig();
    } else if (stepType === StepType.ManualAssetsStep) {
      fieldConfig = FieldConfigUtils.generateAssetsStepFieldConfig();
    } else if (stepType === StepType.ReoStep) {
      fieldConfig = FieldConfigUtils.generateReoStepFieldConfig();
    } else if (stepType === StepType.AddressHistoryStep) {
      fieldConfig = FieldConfigUtils.generateAddressFieldsConfig();
    } else if (stepType === StepType.LoanTypeStep) {
      fieldConfig = FieldConfigUtils.generateLoanInfoStepFieldConfig();
    } else if (stepType === StepType.PurchaseCreditsStep) {
      fieldConfig = FieldConfigUtils.generatePurchaseCreditsStepFieldConfig();
    } else if (stepType === StepType.ReviewApplicationStep) {
      fieldConfig = FieldConfigUtils.generateReviewApplicationStepFieldConfig();
    } else if (stepType === StepType.CreateCoBorrowersStep) {
      fieldConfig = FieldConfigUtils.generateCoBorrowerFieldsConfig();
    } else if (stepType === StepType.ManufacturedHomeDetailsStep) {
      fieldConfig = FieldConfigUtils.generateManufacturedHomeDetailsStepFieldConfig();
    } else if (stepType === StepType.DeclarationsStep) {
      fieldConfig = FieldConfigUtils.generateDeclarationsStepFieldConfig();
    } else if (stepType === StepType.AddressStep) {
      fieldConfig = FieldConfigUtils.generateAddressStepFieldConfig();
    } else if (stepType === StepType.CreateAccount) {
      fieldConfig = FieldConfigUtils.generateCreateAccountStepFieldConfig();
    }
    return fieldConfig;
  }

  public static isHidden = (fieldConfig: any, fieldName: string): boolean => {
    if (fieldConfig[fieldName]) {
      return fieldConfig[fieldName].isHidden;
    }
    return false;
  }

  public static isRequied = (fieldConfig: any, fieldName: string): boolean => {
    if (fieldConfig[fieldName]) {
      return fieldConfig[fieldName].isRequied;
    }
    return false;
  }
}
